import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ArrowLeft, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";

const UGL_LOGO = "/ugl-logo.png";

export default function LoginPage() {
  const [cf, setCf] = useState("");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cf.trim()) { setError("Inserisci il codice fiscale"); return; }
    if (pin.length !== 8) { setError("Il PIN deve essere di 8 cifre"); return; }
    if (password.length !== 4) { setError("La password deve essere di 4 caratteri"); return; }
    setError("");
    setLoading(true);
    try {
      await login(cf.toUpperCase().trim(), pin, password.toUpperCase());
      toast.success("Accesso effettuato!");
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg;
      if (typeof detail === "string") msg = detail;
      else if (err.message === "Network Error") msg = "Errore di rete. Controlla la connessione.";
      else msg = "Errore di accesso. Riprova.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen section-dark relative overflow-hidden flex items-center justify-center px-4" role="main" aria-label="Pagina di login">
      <div className="section-grid-bg" />
      <div className="absolute top-8 left-8 w-20 h-20 border-t-2 border-l-2 border-[#C9A84C]/20 hidden lg:block" />
      <div className="absolute bottom-8 right-8 w-20 h-20 border-b-2 border-r-2 border-[#C9A84C]/20 hidden lg:block" />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center text-sm text-white/40 hover:text-[#00A0D1] transition-colors mb-6">
            <ArrowLeft className="w-4 h-4 mr-1" /> Torna alla Home
          </Link>
          <div className="flex justify-center mb-4">
            <img src={UGL_LOGO} alt="Logo UGL" className="w-16 h-16 object-contain" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Accedi al Portale
          </h1>
          <p className="text-sm text-white/40 mt-2">UGL Lariano - Area Riservata</p>
        </div>

        <div className="glass-card rounded-xl border border-[#C9A84C]/20 p-6" data-testid="login-card">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Login Sicuro</h2>
            <div className="flex items-center gap-1.5 mt-1">
              <Shield className="w-3 h-3 text-[#C9A84C]" />
              <p className="text-[10px] text-[#C9A84C]/60">Protetto con PIN + Password + Rate Limiting</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Codice Fiscale */}
            <div>
              <Label htmlFor="cf" className="text-xs font-medium text-white/60">Codice Fiscale</Label>
              <Input
                id="cf" data-testid="login-cf-input" value={cf}
                onChange={(e) => setCf(e.target.value.toUpperCase())}
                placeholder="Es. RSSMRA85M01H501Z"
                aria-required="true"
                className="mt-1.5 uppercase tracking-wider bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-[#00A0D1]"
                maxLength={16} autoComplete="off"
              />
            </div>

            {/* PIN 8 cifre */}
            <div>
              <Label className="text-xs font-medium text-white/60">PIN (8 cifre)</Label>
              <div className="mt-2 flex justify-center">
                <InputOTP maxLength={8} value={pin} onChange={(val) => setPin(val)} data-testid="login-pin-input">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="border-white/15 bg-white/5 text-white w-9 h-11" />
                    <InputOTPSlot index={1} className="border-white/15 bg-white/5 text-white w-9 h-11" />
                    <InputOTPSlot index={2} className="border-white/15 bg-white/5 text-white w-9 h-11" />
                    <InputOTPSlot index={3} className="border-white/15 bg-white/5 text-white w-9 h-11" />
                  </InputOTPGroup>
                  <span className="mx-1.5 text-white/20">-</span>
                  <InputOTPGroup>
                    <InputOTPSlot index={4} className="border-white/15 bg-white/5 text-white w-9 h-11" />
                    <InputOTPSlot index={5} className="border-white/15 bg-white/5 text-white w-9 h-11" />
                    <InputOTPSlot index={6} className="border-white/15 bg-white/5 text-white w-9 h-11" />
                    <InputOTPSlot index={7} className="border-white/15 bg-white/5 text-white w-9 h-11" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>

            {/* Password 4 caratteri */}
            <div>
              <Label className="text-xs font-medium text-white/60">Password (4 lettere maiuscole)</Label>
              <Input
                data-testid="login-password-input" value={password}
                onChange={(e) => setPassword(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4))}
                placeholder="Es. ABCD"
                className="mt-1.5 uppercase tracking-[0.3em] text-center text-lg font-bold bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-[#C9A84C]"
                maxLength={4} autoComplete="off"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 p-3 rounded-md border border-red-500/20">
                {error}
              </div>
            )}
            <Button type="submit" data-testid="login-submit-btn"
              className="w-full bg-gradient-to-r from-[#C9A84C] to-[#B8963C] text-[#001830] hover:from-[#C9A84C]/90 hover:to-[#B8963C]/90 h-11 rounded-md font-semibold"
              disabled={loading}>
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Accesso in corso...</> : "Accedi"}
            </Button>
          </form>
        </div>
        <p className="text-center text-[10px] text-white/25 mt-6">
          Accesso riservato agli iscritti UGL Lariano
        </p>
      </div>
    </div>
  );
}
